import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../store";
import type { RootState } from "../../store";
import {
  fetchAgents,
  fetchUserAgentMappings,
  deleteUsers,
  updateUserAgents,
  bulkUploadUsers,
  assignMultipleAgents,
  resetDeleteState,
  resetBulkUploadState,
  resetUpdateState,
} from "../../store/userRolesSlice";
import { useMsal } from "@azure/msal-react";
import { clearUser } from "../../store/authSlice";
import Sidebar from "../../components/Sidebar";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  Divider,
  TextField,
  Autocomplete,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TablePagination,
  TableSortLabel,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import UploadIcon from "@mui/icons-material/Upload";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import "../Dashboard.scss";
import styles from "./userroles.module.scss";
import type { Agent } from "../../store/userRolesSlice";
import { useNavigate } from "react-router-dom";

// Debounce hook for search
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

// Email validation helper
const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export default function UserRolesPage(): JSX.Element {
  const user = useSelector((state: RootState) => state.auth.user);
  const { instance } = useMsal();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const {
    agents,
    agentsLoading,
    userAgentMappings,
    mappingsPagination,
    mappingsLoading,
    error,
    deleteLoading,
    deleteSuccess,
    deleteError,
    deletingUserIds,
    deletingAgentId,
    updateLoading,
    updateSuccess,
    updateError,
    bulkUploadLoading,
    bulkUploadSuccess,
    bulkUploadError,
    bulkUploadResult,
  } = useSelector((state: RootState) => state.userRoles);

  // Bulk upload state
  const [selectedAgents, setSelectedAgents] = useState<Agent[]>([]);
  const [emails, setEmails] = useState<string[]>([""]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [emailErrors, setEmailErrors] = useState<{ [key: number]: string }>({});
  const [lastBulkOperation, setLastBulkOperation] = useState<
    "add" | "remove" | null
  >(null);

  // Table state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [searchInputValue, setSearchInputValue] = useState(""); // Immediate input value
  const [sortBy, setSortBy] = useState<string>("userName");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Filter state
  const [selectedAgentFilters, setSelectedAgentFilters] = useState<Agent[]>([]);
  const [selectedCategoryFilter, setSelectedCategoryFilter] =
    useState<string>("all");
  const [activeFilterType, setActiveFilterType] = useState<
    "category" | "agents" | null
  >(null);

  const debouncedSearchTerm = useDebounce(searchInputValue, 500);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    type: "user" | "agent" | null;
    userId?: string;
    agentId?: string;
    username?: string;
    agentName?: string;
    userEmail?: string;
  }>({
    open: false,
    type: null,
  });

  const loadMappings = useCallback(() => {
    // Prepare search criteria
    const searchByUserName = debouncedSearchTerm.trim();

    // Prepare agent filter IDs
    const agentIds = selectedAgentFilters.map((agent) =>
      parseInt(agent.id, 10),
    );

    // Prepare category filter
    const categories =
      selectedCategoryFilter !== "all" ? [selectedCategoryFilter] : [];

    const params: any = {
      // When searching client-side, fetch all results (up to 1000)
      // Otherwise use pagination
      limit: searchByUserName ? 1000 : rowsPerPage,
      offset: searchByUserName ? 0 : page * rowsPerPage,
      sortBy,
      sortOrder,
    };

    // Note: Backend doesn't filter, so we don't send search params
    // We'll filter client-side instead
    if (agentIds.length > 0) {
      params.search_by_agent_id = agentIds;
    }
    if (categories.length > 0) {
      params.search_by_agent_category = categories;
    }

    dispatch(fetchUserAgentMappings(params));
  }, [
    dispatch,
    rowsPerPage,
    page,
    debouncedSearchTerm,
    sortBy,
    sortOrder,
    selectedAgentFilters,
    selectedCategoryFilter,
  ]);

  useEffect(() => {
    dispatch(fetchAgents(undefined));
  }, [dispatch]);

  useEffect(() => {
    loadMappings();
  }, [loadMappings]);

  // Reset page when debounced search term changes
  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm]);

  useEffect(() => {
    if (bulkUploadSuccess) {
      // Clear form on success only if it was an add operation
      if (lastBulkOperation === "add") {
        setSelectedAgents([]);
        setEmails([""]);
        setSelectedFile(null);
        setEmailErrors({});
        // Reset file input element
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }

      // Refresh mappings
      loadMappings();

      const timer = setTimeout(() => {
        dispatch(resetBulkUploadState());
        setLastBulkOperation(null);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [bulkUploadSuccess, dispatch, loadMappings, lastBulkOperation]);

  useEffect(() => {
    if (deleteSuccess) {
      // Reset to first page to avoid pagination issues
      setPage(0);

      // Refresh mappings immediately after delete
      const refreshTimer = setTimeout(() => {
        loadMappings();
      }, 100);

      const resetTimer = setTimeout(() => {
        dispatch(resetDeleteState());
      }, 3000);

      return () => {
        clearTimeout(refreshTimer);
        clearTimeout(resetTimer);
      };
    }
  }, [deleteSuccess, dispatch, loadMappings]);

  useEffect(() => {
    if (updateSuccess && lastBulkOperation === "remove") {
      // Reset to first page to avoid pagination issues
      setPage(0);

      // Refresh mappings immediately after agent removal
      const refreshTimer = setTimeout(() => {
        loadMappings();
      }, 100);

      const resetTimer = setTimeout(() => {
        dispatch(resetUpdateState());
        setLastBulkOperation(null);
      }, 3000);

      return () => {
        clearTimeout(refreshTimer);
        clearTimeout(resetTimer);
      };
    }
  }, [updateSuccess, dispatch, loadMappings, lastBulkOperation]);

  const handleEmailChange = (index: number, value: string) => {
    const newEmails = [...emails];
    newEmails[index] = value;
    setEmails(newEmails);

    // Validate email
    if (value && !validateEmail(value)) {
      setEmailErrors((prev) => ({
        ...prev,
        [index]: "Please enter a valid email address",
      }));
    } else {
      setEmailErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
    }
  };

  const handleAddEmail = () => {
    setEmails([...emails, ""]);
  };

  const handleRemoveEmail = (index: number) => {
    if (emails.length > 1) {
      const newEmails = emails.filter((_, i) => i !== index);
      setEmails(newEmails);

      // Remove error for this index
      setEmailErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[index];
        return newErrors;
      });
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = [
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];
      if (
        !validTypes.includes(file.type) &&
        !file.name.endsWith(".xlsx") &&
        !file.name.endsWith(".xls")
      ) {
        alert("Please upload a valid Excel file (.xlsx or .xls)");
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleBulkUpload = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedAgents.length === 0) {
      alert("Please select at least one agent");
      return;
    }

    // Validate that either file is uploaded OR emails are provided
    const validEmails = emails.filter(
      (email) => email.trim() && validateEmail(email),
    );

    if (!selectedFile && validEmails.length === 0) {
      alert(
        "Please either upload an Excel file or add at least one valid email address",
      );
      return;
    }

    setLastBulkOperation("add");

    // Determine if we should use departments or agent_ids
    const agentIdsByCategory = new Map<string, Set<string>>();

    // Group selected agents by category
    selectedAgents.forEach((agent) => {
      const category = agent.category?.trim().normalize("NFKC") || "Other";
      if (!agentIdsByCategory.has(category)) {
        agentIdsByCategory.set(category, new Set());
      }
      agentIdsByCategory.get(category)!.add(agent.id);
    });

    // Check which categories have all their agents selected
    const fullySelectedCategories: string[] = [];
    let hasPartialSelection = false;

    agentIdsByCategory.forEach((selectedIds, category) => {
      const categoryAgents = agentsByCategory.get(category) || [];
      const allAgentIds = new Set(categoryAgents.map((a) => a.id));

      if (
        selectedIds.size === allAgentIds.size &&
        [...selectedIds].every((id) => allAgentIds.has(id))
      ) {
        fullySelectedCategories.push(category);
      } else {
        hasPartialSelection = true;
      }
    });

    // Use departments if all selected agents belong to complete categories
    const useDepartments = fullySelectedCategories.length > 0 && !hasPartialSelection;

    // Prepare the payload
    const payload: any = {};

    if (selectedFile) {
      payload.file = selectedFile;
    } else {
      payload.addEmails = validEmails;
    }

    if (useDepartments) {
      payload.departments = fullySelectedCategories;
    } else {
      payload.agentIds = selectedAgents.map((agent) => parseInt(agent.id, 10));
    }

    // Call the new optimized API
    await dispatch(assignMultipleAgents(payload));
  };

  const realAgents = useMemo(() => {
    const agentsList = agents || [];
    return [...agentsList].sort((a, b) => {
      const catA = a.category?.trim().normalize("NFKC") || "Other";
      const catB = b.category?.trim().normalize("NFKC") || "Other";
      if (catA !== catB) return catA.localeCompare(catB);
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [agents]);

  // Get unique categories from agents
  const categories = useMemo(() => {
    const categorySet = new Set<string>();
    realAgents.forEach((agent) => {
      if (agent.category?.trim()) {
        categorySet.add(agent.category.trim().normalize("NFKC"));
      }
    });
    return Array.from(categorySet).sort();
  }, [realAgents]);

  // Group agents by category for efficient category operations
  const agentsByCategory = useMemo(() => {
    const grouped = new Map<string, Agent[]>();
    realAgents.forEach((agent) => {
      const category = agent.category?.trim().normalize("NFKC") || "Other";
      if (!grouped.has(category)) {
        grouped.set(category, []);
      }
      grouped.get(category)!.push(agent);
    });
    return grouped;
  }, [realAgents]);

  // Check if all agents in a category are selected
  const isCategorySelected = useCallback(
    (category: string): boolean => {
      const categoryAgents = agentsByCategory.get(category) || [];
      return (
        categoryAgents.length > 0 &&
        categoryAgents.every((agent) =>
          selectedAgents.some((selected) => selected.id === agent.id),
        )
      );
    },
    [agentsByCategory, selectedAgents],
  );

  // Check if some (but not all) agents in a category are selected
  const isCategoryIndeterminate = useCallback(
    (category: string): boolean => {
      const categoryAgents = agentsByCategory.get(category) || [];
      const selectedCount = categoryAgents.filter((agent) =>
        selectedAgents.some((selected) => selected.id === agent.id),
      ).length;
      return selectedCount > 0 && selectedCount < categoryAgents.length;
    },
    [agentsByCategory, selectedAgents],
  );

  // Handle category toggle
  const handleCategoryToggle = useCallback(
    (category: string) => {
      const categoryAgents = agentsByCategory.get(category) || [];
      const allSelected = isCategorySelected(category);

      if (allSelected) {
        // Deselect all agents in this category
        setSelectedAgents((prev) =>
          prev.filter(
            (agent) =>
              !categoryAgents.some((catAgent) => catAgent.id === agent.id),
          ),
        );
      } else {
        // Select all agents in this category
        setSelectedAgents((prev) => {
          const newAgents = categoryAgents.filter(
            (catAgent) => !prev.some((selected) => selected.id === catAgent.id),
          );
          return [...prev, ...newAgents];
        });
      }
    },
    [agentsByCategory, isCategorySelected],
  );

  const isBulkUploadDisabled = useMemo(() => {
    if (selectedAgents.length === 0) return true;
    if (bulkUploadLoading) return true;

    // Check if file is uploaded
    if (selectedFile) return false;

    // Check if at least one valid email exists
    const validEmails = emails.filter(
      (email) => email.trim() && validateEmail(email),
    );
    return validEmails.length === 0;
  }, [selectedAgents, bulkUploadLoading, selectedFile, emails]);

  const onClickLogout = async () => {
    try {
      await instance.logoutPopup({
        postLogoutRedirectUri: "/",
      });
      dispatch(clearUser());
      window.location.href = "/";
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  const handleDeleteUserClick = useCallback(
    (userId: string, username: string) => {
      setConfirmDialog({
        open: true,
        type: "user",
        userId,
        username,
      });
    },
    [],
  );

  const handleDeleteAgentClick = useCallback(
    (userId: string, agentId: string, agentName: string, userEmail: string) => {
      setConfirmDialog({
        open: true,
        type: "agent",
        userId,
        agentId,
        agentName,
        userEmail,
      });
    },
    [],
  );

  const handleConfirmDelete = useCallback(async () => {
    try {
      if (confirmDialog.type === "user" && confirmDialog.userId) {
        await dispatch(deleteUsers({ userIds: [confirmDialog.userId] })).unwrap();
        setConfirmDialog({ open: false, type: null });
      } else if (
        confirmDialog.type === "agent" &&
        confirmDialog.agentId &&
        confirmDialog.userId
      ) {
        // Use updateUserAgents API to remove the agent from user
        setLastBulkOperation("remove");
        await dispatch(
          updateUserAgents({
            userId: confirmDialog.userId,
            remove: [confirmDialog.agentId],
          }),
        ).unwrap();
        // Close dialog only after successful removal
        setConfirmDialog({ open: false, type: null });
      }
    } catch (error) {
      // Error is handled by Redux state, close the dialog
      setConfirmDialog({ open: false, type: null });
    }
  }, [confirmDialog, dispatch]);

  const handleCancelDelete = useCallback(() => {
    setConfirmDialog({ open: false, type: null });
  }, []);

  const handleChangePage = useCallback((_event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    },
    [],
  );

  const handleSortChange = useCallback(
    (column: string) => {
      setSortBy((prevSortBy) => {
        const isAsc = prevSortBy === column && sortOrder === "asc";
        setSortOrder(isAsc ? "desc" : "asc");
        return column;
      });
      setPage(0);
    },
    [sortOrder],
  );

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchInputValue(event.target.value);
    },
    [],
  );

  const handleClearSearch = useCallback(() => {
    setSearchInputValue("");
    setPage(0);
  }, []);

  const handleClearFilters = useCallback(() => {
    setSelectedAgentFilters([]);
    setSelectedCategoryFilter("all");
    setActiveFilterType(null);
  }, []);

  const handleCategoryFilterChange = useCallback((e: any) => {
    setSelectedCategoryFilter(e.target.value);
  }, []);

  const handleAgentFilterChange = useCallback(
    (_event: any, newValue: Agent[]) => {
      setSelectedAgentFilters(newValue);
    },
    [],
  );

  return (
    <div className="dashboard-root">
      <Sidebar
        activeNav="user-management"
        user={user || undefined}
        onLogout={onClickLogout}
      />
      <main className="main-area">
        <header className="header">
          <div className="header-left">
            <IconButton
              onClick={() => navigate("/landing")}
              sx={{ mr: 2 }}
              aria-label="Back to landing page"
            >
              <ArrowBackIcon />
            </IconButton>
            <h1 className="page-title">User Roles Management</h1>
          </div>
        </header>

        <div className="scrollContent">
          <div className={styles.content}>
            {agentsLoading && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="400px"
              >
                <CircularProgress />
              </Box>
            )}

            {!agentsLoading && error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {!agentsLoading && (
              <>
                {updateSuccess && lastBulkOperation === "remove" && (
                  <Alert
                    severity="success"
                    sx={{ mb: 3 }}
                    onClose={() => {
                      dispatch(resetUpdateState());
                      setLastBulkOperation(null);
                    }}
                  >
                    Agent access removed successfully!
                  </Alert>
                )}
                {bulkUploadSuccess && lastBulkOperation === "remove" && (
                  <Alert
                    severity="success"
                    sx={{ mb: 3 }}
                    onClose={() => {
                      dispatch(resetBulkUploadState());
                      setLastBulkOperation(null);
                    }}
                  >
                    Agent access removed successfully!
                  </Alert>
                )}
                {deleteSuccess && (
                  <Alert
                    severity="success"
                    sx={{ mb: 3 }}
                    onClose={() => dispatch(resetDeleteState())}
                  >
                    User deleted successfully!
                  </Alert>
                )}
                {deleteError && (
                  <Alert
                    severity="error"
                    sx={{ mb: 3 }}
                    onClose={() => dispatch(resetDeleteState())}
                  >
                    {deleteError}
                  </Alert>
                )}
                {updateError && lastBulkOperation === "remove" && (
                  <Alert
                    severity="error"
                    sx={{ mb: 3 }}
                    onClose={() => {
                      dispatch(resetUpdateState());
                      setLastBulkOperation(null);
                    }}
                  >
                    Failed to remove agent access: {updateError}
                  </Alert>
                )}
                {bulkUploadError && lastBulkOperation === "remove" && (
                  <Alert
                    severity="error"
                    sx={{ mb: 3 }}
                    onClose={() => {
                      dispatch(resetBulkUploadState());
                      setLastBulkOperation(null);
                    }}
                  >
                    Failed to remove agent access: {bulkUploadError}
                  </Alert>
                )}

                <Card className={styles.bulkUploadCard} elevation={2}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                      Assign Users to Agent
                    </Typography>

                    <form onSubmit={handleBulkUpload}>
                      {bulkUploadError && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                          {bulkUploadError}
                        </Alert>
                      )}

                      {bulkUploadSuccess &&
                        lastBulkOperation === "add" &&
                        bulkUploadResult && (
                          <Alert
                            severity={
                              bulkUploadResult.failed > 0
                                ? "warning"
                                : bulkUploadResult.agentsAssigned > 0 ||
                                    bulkUploadResult.usersCreated > 0
                                  ? "success"
                                  : "info"
                            }
                            sx={{ mb: 3 }}
                            onClose={() => {
                              dispatch(resetBulkUploadState());
                              setLastBulkOperation(null);
                            }}
                          >
                            <Box>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                gutterBottom
                              >
                                Upload Complete
                              </Typography>
                              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                                {bulkUploadResult.usersCreated > 0 && (
                                  <li>
                                    <Typography variant="body2">
                                      {bulkUploadResult.usersCreated} new
                                      user(s) created
                                    </Typography>
                                  </li>
                                )}
                                {bulkUploadResult.agentsAssigned > 0 && (
                                  <li>
                                    <Typography variant="body2">
                                      {bulkUploadResult.agentsAssigned} agent
                                      assignment(s) completed
                                    </Typography>
                                  </li>
                                )}
                                {bulkUploadResult.usersExisted > 0 &&
                                  bulkUploadResult.agentsAssigned === 0 && (
                                    <li>
                                      <Typography variant="body2">
                                        {bulkUploadResult.usersExisted} user(s)
                                        already had this agent assigned
                                      </Typography>
                                    </li>
                                  )}
                                {bulkUploadResult.failed > 0 && (
                                  <li>
                                    <Typography variant="body2" color="error">
                                      {bulkUploadResult.failed} operation(s)
                                      failed
                                    </Typography>
                                  </li>
                                )}
                              </Box>
                            </Box>
                          </Alert>
                        )}

                      <Box className={styles.formRow}>
                        <Autocomplete
                          multiple
                          id="agent-select"
                          options={realAgents}
                          value={selectedAgents}
                          onChange={(_event, newValue) => {
                            setSelectedAgents(newValue);
                          }}
                          getOptionLabel={(option) => option.name}
                          getOptionKey={(option) => option.id}
                          groupBy={(option) =>
                            option.category?.trim().normalize("NFKC") || "Other"
                          }
                          isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                          }
                          disabled={bulkUploadLoading}
                          disableCloseOnSelect
                          renderOption={(props, option) => {
                            const { key, ...otherProps } = props;
                            const isSelected = selectedAgents.some(
                              (agent) => agent.id === option.id,
                            );
                            return (
                              <li key={key} {...otherProps}>
                                <Checkbox checked={isSelected} sx={{ mr: 1 }} />
                                <ListItemText primary={option.name} />
                              </li>
                            );
                          }}
                          renderGroup={(params) => {
                            const category = params.group;
                            const isSelected = isCategorySelected(category);
                            const isIndeterminate =
                              isCategoryIndeterminate(category);

                            return (
                              <li key={params.key}>
                                <Box
                                  sx={{
                                    position: "sticky",
                                    top: 0,
                                    bgcolor: "background.paper",
                                    zIndex: 1,
                                    borderBottom: "1px solid",
                                    borderColor: "divider",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      px: 2,
                                      py: 1,
                                      cursor: "pointer",
                                      "&:hover": {
                                        bgcolor: "action.hover",
                                      },
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCategoryToggle(category);
                                    }}
                                  >
                                    <Checkbox
                                      checked={isSelected}
                                      indeterminate={isIndeterminate}
                                      sx={{ mr: 1 }}
                                    />
                                    <Typography
                                      variant="subtitle2"
                                      fontWeight={600}
                                      color="primary"
                                    >
                                      {category}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ ml: 1 }}
                                    >
                                      (
                                      {agentsByCategory.get(category)?.length ||
                                        0}
                                      )
                                    </Typography>
                                  </Box>
                                </Box>
                                <ul style={{ padding: 0 }}>
                                  {params.children}
                                </ul>
                              </li>
                            );
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Select Agents"
                              placeholder={
                                selectedAgents.length === 0
                                  ? "Choose agents or categories..."
                                  : ""
                              }
                            />
                          )}
                        />
                      </Box>

                      <Divider sx={{ my: 3 }}>
                        <Typography variant="caption" color="text.secondary">
                          Choose one option below
                        </Typography>
                      </Divider>

                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Option 1: Upload Excel File
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            mt: 1,
                          }}
                        >
                          <Button
                            variant="outlined"
                            component="label"
                            startIcon={<CloudUploadIcon />}
                            disabled={bulkUploadLoading}
                          >
                            Choose Excel File
                            <input
                              ref={fileInputRef}
                              type="file"
                              hidden
                              accept=".xlsx,.xls"
                              onChange={handleFileChange}
                            />
                          </Button>
                          {selectedFile && (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Typography variant="body2">
                                {selectedFile.name}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={handleClearFile}
                                disabled={bulkUploadLoading}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          )}
                        </Box>
                      </Box>

                      <Divider sx={{ my: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          OR
                        </Typography>
                      </Divider>

                      <Box sx={{ mb: 3 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 1,
                          }}
                        >
                          <Typography variant="subtitle2">
                            Option 2: Add Email Addresses Manually
                          </Typography>
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={handleAddEmail}
                            disabled={bulkUploadLoading}
                          >
                            Add Email
                          </Button>
                        </Box>
                        {emails.map((email, index) => (
                          <Box
                            key={index}
                            sx={{
                              display: "flex",
                              gap: 1,
                              mb: 2,
                              alignItems: "flex-start",
                            }}
                          >
                            <TextField
                              fullWidth
                              label={`Email ${index + 1}`}
                              placeholder="e.g., user@example.com"
                              value={email}
                              onChange={(e) =>
                                handleEmailChange(index, e.target.value)
                              }
                              error={!!emailErrors[index]}
                              helperText={emailErrors[index]}
                              disabled={bulkUploadLoading}
                              type="email"
                            />
                            {emails.length > 1 && (
                              <IconButton
                                color="error"
                                onClick={() => handleRemoveEmail(index)}
                                disabled={bulkUploadLoading}
                                sx={{ mt: 1 }}
                              >
                                <CloseIcon />
                              </IconButton>
                            )}
                          </Box>
                        ))}
                      </Box>

                      <Box className={styles.submitRow}>
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          size="large"
                          disabled={isBulkUploadDisabled}
                          startIcon={
                            bulkUploadLoading ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              <UploadIcon />
                            )
                          }
                        >
                          {bulkUploadLoading
                            ? selectedAgents.length > 1
                              ? `Assigning to ${selectedAgents.length} agents...`
                              : "Assigning..."
                            : "Assign Users"}
                        </Button>
                      </Box>
                    </form>
                  </CardContent>
                </Card>

                <Divider sx={{ my: 4 }} />

                <Card className={styles.mappingCard} elevation={2}>
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 3,
                      }}
                    >
                      <Typography variant="h6">
                        Current User-Agent Assignments
                      </Typography>
                      <TextField
                        size="small"
                        placeholder="Search by user or email"
                        value={searchInputValue}
                        onChange={handleSearchChange}
                        sx={{ minWidth: 300 }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" />
                            </InputAdornment>
                          ),
                          endAdornment: searchInputValue && (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={handleClearSearch}
                                edge="end"
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>

                    {/* Filter Section */}
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1.5,
                        mb: 2,
                        p: 1.5,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        bgcolor: "grey.50",
                        alignItems: "flex-start",
                      }}
                    >
                      {/* Left Panel - Filter Type Tabs */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                          minWidth: 110,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: "text.secondary",
                            px: 1,
                            mb: 0.5,
                          }}
                        >
                          FILTER BY
                        </Typography>
                        <Box
                          onClick={() =>
                            setActiveFilterType(
                              activeFilterType === "category"
                                ? null
                                : "category",
                            )
                          }
                          sx={{
                            px: 1.5,
                            py: 0.75,
                            cursor: "pointer",
                            borderRadius: 0.5,
                            bgcolor:
                              activeFilterType === "category"
                                ? "primary.main"
                                : "transparent",
                            color:
                              activeFilterType === "category"
                                ? "white"
                                : "text.primary",
                            transition: "all 0.15s",
                            "&:hover": {
                              bgcolor:
                                activeFilterType === "category"
                                  ? "primary.dark"
                                  : "grey.200",
                            },
                          }}
                        >
                          <Typography variant="body2" fontSize="0.8125rem">
                            Category
                            {selectedCategoryFilter !== "all" && " ✓"}
                          </Typography>
                        </Box>
                        <Box
                          onClick={() =>
                            setActiveFilterType(
                              activeFilterType === "agents" ? null : "agents",
                            )
                          }
                          sx={{
                            px: 1.5,
                            py: 0.75,
                            cursor: "pointer",
                            borderRadius: 0.5,
                            bgcolor:
                              activeFilterType === "agents"
                                ? "primary.main"
                                : "transparent",
                            color:
                              activeFilterType === "agents"
                                ? "white"
                                : "text.primary",
                            transition: "all 0.15s",
                            "&:hover": {
                              bgcolor:
                                activeFilterType === "agents"
                                  ? "primary.dark"
                                  : "grey.200",
                            },
                          }}
                        >
                          <Typography variant="body2" fontSize="0.8125rem">
                            Agents
                            {selectedAgentFilters.length > 0 &&
                              ` (${selectedAgentFilters.length})`}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Vertical Divider */}
                      <Divider orientation="vertical" flexItem />

                      {/* Right Panel - Filter Controls */}
                      <Box
                        sx={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        {!activeFilterType ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              minHeight: 40,
                            }}
                          >
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              fontSize="0.8125rem"
                            >
                              Select a filter type to configure
                            </Typography>
                          </Box>
                        ) : activeFilterType === "category" ? (
                          <FormControl fullWidth size="small">
                            <InputLabel id="category-filter-label">
                              Select Category
                            </InputLabel>
                            <Select
                              labelId="category-filter-label"
                              value={selectedCategoryFilter}
                              onChange={handleCategoryFilterChange}
                              label="Select Category"
                            >
                              <MenuItem value="all">All Categories</MenuItem>
                              {categories.map((category) => (
                                <MenuItem key={category} value={category}>
                                  {category}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        ) : activeFilterType === "agents" ? (
                          <Autocomplete
                            multiple
                            size="small"
                            options={realAgents}
                            value={selectedAgentFilters}
                            onChange={handleAgentFilterChange}
                            getOptionLabel={(option) => option.name}
                            getOptionKey={(option) => option.id}
                            isOptionEqualToValue={(option, value) =>
                              option.id === value.id
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Select Agents"
                                placeholder="Search..."
                              />
                            )}
                            renderTags={(value, getTagProps) =>
                              value.map((option, index) => {
                                const { key, ...tagProps } = getTagProps({ index });
                                return (
                                  <Chip
                                    key={key}
                                    {...tagProps}
                                    label={option.name}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                );
                              })
                            }
                          />
                        ) : null}
                      </Box>

                      {/* Clear Filters Button */}
                      {(selectedCategoryFilter !== "all" ||
                        selectedAgentFilters.length > 0) && (
                        <>
                          <Divider orientation="vertical" flexItem />
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleClearFilters}
                            sx={{
                              minWidth: "auto",
                              px: 1.5,
                              alignSelf: "center",
                            }}
                          >
                            Clear
                          </Button>
                        </>
                      )}
                    </Box>

                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              <TableSortLabel
                                active={sortBy === "userName"}
                                direction={
                                  sortBy === "userName" ? sortOrder : "asc"
                                }
                                onClick={() => handleSortChange("userName")}
                                disabled={mappingsLoading}
                              >
                                <strong>User</strong>
                              </TableSortLabel>
                            </TableCell>
                            <TableCell>
                              <TableSortLabel
                                active={sortBy === "email"}
                                direction={
                                  sortBy === "email" ? sortOrder : "asc"
                                }
                                onClick={() => handleSortChange("email")}
                                disabled={mappingsLoading}
                              >
                                <strong>Email</strong>
                              </TableSortLabel>
                            </TableCell>
                            <TableCell>
                              <strong>Assigned Agents</strong>
                            </TableCell>
                            <TableCell align="center" sx={{ width: 100 }}>
                              <strong>Actions</strong>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {mappingsLoading && userAgentMappings.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={4} align="center">
                                <Box sx={{ py: 4 }}>
                                  <CircularProgress size={40} />
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mt: 2 }}
                                  >
                                    Loading...
                                  </Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ) : (() => {
                            // CLIENT-SIDE FILTERING (workaround for backend not filtering)
                            const searchTerm = debouncedSearchTerm.toLowerCase().trim();
                            const filteredMappings = searchTerm
                              ? userAgentMappings.filter((mapping) => {
                                  const nameMatch = mapping.user.name
                                    ?.toLowerCase()
                                    .includes(searchTerm);
                                  const emailMatch = mapping.user.email
                                    ?.toLowerCase()
                                    .includes(searchTerm);
                                  return nameMatch || emailMatch;
                                })
                              : userAgentMappings;

                            return filteredMappings.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={4} align="center">
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ py: 3 }}
                                  >
                                    {debouncedSearchTerm ||
                                    selectedCategoryFilter !== "all" ||
                                    selectedAgentFilters.length > 0
                                      ? "No results found for your search or filters"
                                      : "No user-agent assignments found"}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ) : (
                              filteredMappings.map((mapping) => (
                              <TableRow key={mapping.user.id}>
                                <TableCell>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <PersonIcon
                                      fontSize="small"
                                      color="action"
                                    />
                                    <Typography variant="body2">
                                      {mapping.user.name}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {mapping.user.email}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 0.5,
                                    }}
                                  >
                                    {mapping.agents.length === 0 ? (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        No agents assigned
                                      </Typography>
                                    ) : (
                                      mapping.agents.map((agent) => (
                                        <Chip
                                          key={agent.id}
                                          label={agent.name}
                                          size="small"
                                          color="primary"
                                          variant="outlined"
                                          onDelete={() =>
                                            handleDeleteAgentClick(
                                              mapping.user.id,
                                              agent.id,
                                              agent.name,
                                              mapping.user.email,
                                            )
                                          }
                                          disabled={
                                            (deleteLoading &&
                                              deletingAgentId === agent.id) ||
                                            (updateLoading &&
                                              lastBulkOperation === "remove") ||
                                            (bulkUploadLoading &&
                                              lastBulkOperation === "remove")
                                          }
                                        />
                                      ))
                                    )}
                                  </Box>
                                </TableCell>
                                <TableCell align="center">
                                  <Tooltip title="Delete user from system">
                                    <IconButton
                                      color="error"
                                      size="small"
                                      onClick={() =>
                                        handleDeleteUserClick(
                                          mapping.user.id,
                                          mapping.user.name,
                                        )
                                      }
                                      disabled={
                                        deleteLoading &&
                                        deletingUserIds.includes(
                                          mapping.user.id,
                                        )
                                      }
                                    >
                                      {deleteLoading &&
                                      deletingUserIds.includes(
                                        mapping.user.id,
                                      ) ? (
                                        <CircularProgress
                                          size={20}
                                          color="inherit"
                                        />
                                      ) : (
                                        <DeleteOutlineIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                              ))
                            );
                          })()}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <TablePagination
                      rowsPerPageOptions={[10, 25, 50, 100]}
                      component="div"
                      count={(() => {
                        // If searching, count client-side filtered results
                        const searchTerm = debouncedSearchTerm.toLowerCase().trim();
                        if (searchTerm) {
                          return userAgentMappings.filter((mapping) => {
                            const nameMatch = mapping.user.name
                              ?.toLowerCase()
                              .includes(searchTerm);
                            const emailMatch = mapping.user.email
                              ?.toLowerCase()
                              .includes(searchTerm);
                            return nameMatch || emailMatch;
                          }).length;
                        }
                        return mappingsPagination?.total || 0;
                      })()}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      labelRowsPerPage="Rows per page:"
                      labelDisplayedRows={({ from, to, count }) =>
                        `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                      }
                    />
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </div>
      </main>

      <Dialog
        open={confirmDialog.open}
        onClose={(event, reason) => {
          if (reason === "backdropClick" && (deleteLoading || updateLoading || bulkUploadLoading)) {
            return;
          }
          handleCancelDelete();
        }}
        aria-labelledby="delete-confirmation-dialog"
      >
        <DialogTitle id="delete-confirmation-dialog">
          {confirmDialog.type === "user"
            ? "Remove User"
            : "Remove Agent Access"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.type === "user" ? (
              <>
                Are you sure you want to remove user{" "}
                <strong>{confirmDialog.username}</strong> from the system? This
                will permanently delete the user and revoke all their agent
                access.
              </>
            ) : (
              <>
                Are you sure you want to remove{" "}
                <strong>{confirmDialog.agentName}</strong> from this user's
                access?
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelDelete}
            color="inherit"
            disabled={deleteLoading || updateLoading || bulkUploadLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            autoFocus
            disabled={deleteLoading || updateLoading || bulkUploadLoading}
            startIcon={
              (deleteLoading || updateLoading || bulkUploadLoading) ? (
                <CircularProgress size={20} color="inherit" />
              ) : undefined
            }
          >
            {deleteLoading || updateLoading || bulkUploadLoading
              ? "Processing..."
              : confirmDialog.type === "user"
                ? "Delete User"
                : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
